import React, { useEffect, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckIcon from "@mui/icons-material/Check";
import { MAIN_COLOR_RED } from "../assets/styles";
import { recommendationApi, shoppingListApi } from "../api";
import type { PersonaRecommendationProduct } from "../api/types";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";

interface PersonaRecommendationsDialogProps {
    open: boolean;
    onClose: () => void;
    personaId: string | null;
    /** List ID for the route (used for backend add). Use "current" for unsaved list. */
    selectedListId: string | null;
    /** Called when an item is added so parent can update selectedList and Total items. */
    onItemAdded: (productId: string, quantity: number) => void;
}

const PersonaRecommendationsDialog: React.FC<PersonaRecommendationsDialogProps> = ({
    open,
    onClose,
    personaId,
    selectedListId,
    onItemAdded,
}) => {
    const [products, setProducts] = useState<PersonaRecommendationProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
    const [addingId, setAddingId] = useState<string | null>(null);
    const { addItem } = useShoppingListRedux();

    const handleAddToCart = useCallback(
        async (productId: string) => {
            const quantity = 1;
            addItem(productId);
            setAddingId(productId);

            if (selectedListId && selectedListId !== "current") {
                try {
                    await shoppingListApi.addShoppingListItem(selectedListId, productId, {
                        product_id: productId,
                        quantity,
                    });
                    onItemAdded(productId, quantity);
                } catch (err) {
                    console.warn("Failed to add item to list on backend:", err);
                } finally {
                    setAddingId(null);
                }
            } else {
                onItemAdded(productId, quantity);
                setAddingId(null);
            }

            setAddedIds((prev) => new Set(prev).add(productId));
        },
        [addItem, selectedListId, onItemAdded]
    );

    useEffect(() => {
        if (!open || !personaId) {
            setProducts([]);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        recommendationApi
            .getRecommendationsByPersona(personaId)
            .then((data) => {
                if (!cancelled) {
                    setProducts(data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || "Failed to load recommendations.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, personaId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #eee",
                    py: 1.5,
                }}
            >
                <Typography
                    variant="h6"
                    component="span"
                    fontWeight="bold"
                    sx={{ color: MAIN_COLOR_RED }}
                >
                    Recommended for you
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress sx={{ color: MAIN_COLOR_RED }} />
                    </Box>
                )}
                {error && (
                    <Typography color="error" sx={{ py: 2, textAlign: "center" }}>
                        {error}
                    </Typography>
                )}
                {!loading && !error && products.length === 0 && (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                        No recommendations available.
                    </Typography>
                )}
                {!loading && !error && products.length > 0 && (
                    <Grid container spacing={2} sx={{ pt: 0 }}>
                        {products.slice(0, 5).map((product) => (
                            <Grid item xs={6} sm={4} key={product.product_id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        borderRadius: 2,
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="120"
                                        image={
                                            product.image_url ||
                                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12' font-family='sans-serif'%3ENo image%3C/text%3E%3C/svg%3E"
                                        }
                                        alt={product.product_name}
                                        sx={{ objectFit: "contain", bgcolor: "#fafafa" }}
                                    />
                                    <CardContent sx={{ flexGrow: 1, py: 1, "&:last-child": { pb: 1 } }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {product.product_name}
                                        </Typography>
                                        {product.price != null && (
                                            <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mt: 0.5 }}>
                                                €{product.price}
                                            </Typography>
                                        )}
                                        {product.brand && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {product.brand}
                                            </Typography>
                                        )}
                                        <Button
                                            size="small"
                                            variant="contained"
                                            fullWidth
                                            startIcon={
                                                addedIds.has(product.product_id) ? (
                                                    <CheckIcon sx={{ fontSize: "inherit" }} />
                                                ) : (
                                                    <AddShoppingCartIcon sx={{ fontSize: "inherit" }} />
                                                )
                                            }
                                            onClick={() => handleAddToCart(product.product_id)}
                                            disabled={addedIds.has(product.product_id) || addingId === product.product_id}
                                            sx={{
                                                mt: 1,
                                                py: 0.4,
                                                px: 1,
                                                minHeight: 28,
                                                fontSize: "0.7rem",
                                                "& .MuiButton-startIcon": { marginRight: 0.5 },
                                                backgroundColor: addedIds.has(product.product_id)
                                                    ? "success.main"
                                                    : MAIN_COLOR_RED,
                                                color: addedIds.has(product.product_id) ? "white" : undefined,
                                                "&:hover": {
                                                    backgroundColor: addedIds.has(product.product_id)
                                                        ? "success.dark"
                                                        : MAIN_COLOR_RED,
                                                    color: addedIds.has(product.product_id) ? "white" : undefined,
                                                },
                                                "@media (min-width:600px)": {
                                                    py: 0.75,
                                                    px: 1.5,
                                                    minHeight: 36,
                                                    fontSize: "0.8125rem",
                                                    "& .MuiButton-startIcon": { marginRight: 0.75 },
                                                },
                                            }}
                                        >
                                            {addingId === product.product_id
                                                ? "Adding…"
                                                : addedIds.has(product.product_id)
                                                  ? "Added"
                                                  : "Add to cart"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PersonaRecommendationsDialog;
